import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Hotel {
  hotel?: string;
  hotelChain?: string;
  hotelName?: string;
  hotelSlug?: string;
  hotelLogo?: string;
}

interface HotelChain {
  hotelChainId: string;
  name?: string;
  slug: string;
  orderPrefix: string;
  hotels: Hotel[];
  /**
   * Per-chain tax-module state (SPAZ2-3366). Written by both chain writers through
   * `readTaxModuleEnabled`, read through `useIsTaxModuleEnabled`. Optional so a
   * redux-persist session stored before this field existed still typechecks; absent
   * means disabled.
   */
  taxModuleEnabled?: boolean;
}

export type AppConfigState = {
  selectedHotels: Hotel[] | null;
  hotelChain: HotelChain | null;
  sidebarCollapsed: boolean;
  // Onboarding tours already seen, per user: { [userId]: tourId[] }.
  // Optional: users persisted before this field existed rehydrate without it
  // (redux-persist's default autoMergeLevel1 replaces the slice wholesale rather
  // than deep-merging in the initial `{}`), so every reader guards with `?.`.
  seenTours?: Record<string, string[]>;
};

const initialState: AppConfigState = {
  selectedHotels: null,
  hotelChain: null,
  sidebarCollapsed: false,
  seenTours: {},
};

export const appConfigSlice = createSlice({
  name: 'appConfig',
  initialState,
  reducers: {
    setHotelChain: (state, action: PayloadAction<HotelChain>) => {
      return { ...state, hotelChain: action.payload };
    },
    setSelectedHotels: (state, action: PayloadAction<Hotel[]>) => {
      return { ...state, selectedHotels: action.payload };
    },
    resetSelectedHotels: (state) => {
      return { ...state, selectedHotels: null };
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      return { ...state, sidebarCollapsed: action.payload };
    },
    markTourSeen: (state, action: PayloadAction<{ userId: string; tourId: string }>) => {
      const { userId, tourId } = action.payload;
      const current = state.seenTours?.[userId] ?? [];
      if (current.includes(tourId)) return state;
      return {
        ...state,
        seenTours: { ...state.seenTours, [userId]: [...current, tourId] },
      };
    },
  },
});

// Export reducer
export default appConfigSlice;
